import * as FileSystem from 'expo-file-system/legacy';

import { logError } from '@utils/logNonFatalError';
import { AsyncStorage } from '@utils/storage';

const DOWNLOAD_DIR_STORAGE_KEY = '@download_dir';

// Best-effort hint that pre-selects the primary storage's Download folder in
// the SAF picker, so the user doesn't have to navigate there manually. If the
// URI is invalid on a given device/OEM, Android just ignores it and shows the
// default picker root -- never throws.
const DOWNLOAD_DIR_INITIAL_URI =
  'content://com.android.externalstorage.documents/tree/primary:Download/document/primary:Download';

// The whole file is read into a base64 JS string before being written back
// out (SAF's writeAsStringAsync has no streaming/native-copy variant), so
// peak memory is roughly 2.3x the file size (raw + base64) while it's held
// in the JS bridge -- a real OOM risk on the older, lower-RAM devices this
// path exists for (Copilot review, PR #5298). 25MB keeps that peak in the
// tens-of-MB range; anything larger fails with an explicit error instead of
// risking a native crash.
const MAX_LEGACY_SAF_FILE_SIZE_BYTES = 25 * 1024 * 1024;

// Concurrent downloads (useDownloadFiles.downloadFiles fires one
// doDownloadFile per selected file without awaiting each in turn) must not
// each independently check AsyncStorage and race to open the picker --
// requestDirectoryPermissionsAsync throws natively
// (FileSystemPendingPermissionsRequestException) if a request is already
// in flight, and even without that, two pickers stacking is a broken UX
// (Copilot review, PR #5298). Memoize the resolution so every concurrent
// caller awaits the same in-flight request; AsyncStorage (not this
// in-memory promise) is the durable source of truth for "already granted"
// across separate, non-concurrent calls, so the memo is cleared as soon as
// it resolves -- it only needs to live long enough to de-dupe a single
// concurrent batch.
let downloadDirRequest: Promise<string | null> | null = null;

// The size guard above bounds a single file, but useDownloadFiles allows up
// to MAX_CONCURRENT_DOWNLOADS (3) simultaneous downloads -- without this,
// three near-the-limit files could each hold a full base64 copy in memory
// at once (~75-100MB+ combined), the same OOM risk the per-file guard exists
// to prevent (Copilot review, PR #5298). Serialize just the memory-heavy
// create+read+write section across ALL concurrent SAF transfers, so at most
// one file's base64 content is held at a time regardless of how many
// downloads are queued elsewhere. A FIFO chain, not a dedup memo like
// resolveDownloadDir above -- every call must still run, just one at a time,
// in the order they arrived; `.then(task, task)` runs `task` next regardless
// of whether the previous transfer resolved or rejected, so one failure
// never wedges the queue for the rest.
let safTransferQueue: Promise<void> = Promise.resolve();

const runSerializedSafTransfer = <T>(task: () => Promise<T>): Promise<T> => {
  const run = safTransferQueue.then(task, task);
  safTransferQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

const resolveDownloadDir = async (): Promise<string | null> => {
  if (!downloadDirRequest) {
    downloadDirRequest = (async () => {
      const cached = await AsyncStorage.getItem(DOWNLOAD_DIR_STORAGE_KEY);
      if (cached) return cached;

      const permission =
        await FileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync(
          DOWNLOAD_DIR_INITIAL_URI
        );
      if (!permission.granted) return null;

      await AsyncStorage.setItem(
        DOWNLOAD_DIR_STORAGE_KEY,
        permission.directoryUri
      );
      return permission.directoryUri;
    })().finally(() => {
      downloadDirRequest = null;
    });
  }

  return downloadDirRequest;
};

/**
 * Storage Access Framework fallback for Android 7-9 (API <29), where
 * RNBlobUtil.MediaCollection.copyToMediaStore (the MediaStore Downloads
 * collection, API 29+ only) is unavailable, and WRITE_EXTERNAL_STORAGE /
 * READ_EXTERNAL_STORAGE are permanently blocked from the manifest
 * (app.config.js blockedPermissions, TA-10087 -- Google Play rejected a
 * production build over broad media permissions), so a legacy direct write
 * to the public Downloads directory can never work either: it would need
 * exactly the permission that's blocked.
 *
 * Deliberately NOT used on API 29+: an earlier version of this fix (TA-9921,
 * see git history on useSaveImage.tsx/useDownloadFiles.tsx) tried SAF
 * universally across all Android versions and found that Android 11+ blocks
 * ACTION_OPEN_DOCUMENT_TREE from granting access to the Downloads directory
 * specifically -- the picker opens, but Downloads can't be selected as the
 * target. That restriction is Android-11-specific and postdates the scoped-
 * storage cutover; it doesn't apply below API 29, so SAF is reused here only
 * for the version range where it still works, with MediaCollection covering
 * everything at or above API 29 instead.
 *
 * The directory-permission grant is persisted (AsyncStorage, mirroring
 * Android's own persistable URI permission) so the picker only appears once
 * per install, not on every download.
 */
export const saveToLegacyAndroidDownloads = async (
  sourceUri: string,
  fileName: string,
  mimeType: string
): Promise<boolean> => {
  try {
    // sourceUri is always our own already-downloaded local file:// URI, never
    // the SAF destination -- decode first in case it carries percent escapes
    // (e.g. a filename with spaces), matching the API 29+ path's fix for the
    // same class of bug.
    const decodedSourceUri = decodeURI(sourceUri);

    const sourceInfo = await FileSystem.getInfoAsync(decodedSourceUri);
    if (
      sourceInfo.exists &&
      !sourceInfo.isDirectory &&
      sourceInfo.size > MAX_LEGACY_SAF_FILE_SIZE_BYTES
    ) {
      throw new Error(
        `File too large for the Android<29 SAF fallback (${sourceInfo.size} bytes, ` +
          `max ${MAX_LEGACY_SAF_FILE_SIZE_BYTES})`
      );
    }

    const downloadDir = await resolveDownloadDir();
    if (!downloadDir) return false;

    return await runSerializedSafTransfer(async () => {
      const destinationUri =
        await FileSystem.StorageAccessFramework.createFileAsync(
          downloadDir,
          fileName,
          mimeType
        );

      const contents = await FileSystem.readAsStringAsync(decodedSourceUri, {
        encoding: FileSystem.EncodingType.Base64,
      });

      await FileSystem.StorageAccessFramework.writeAsStringAsync(
        destinationUri,
        contents,
        { encoding: FileSystem.EncodingType.Base64 }
      );

      return true;
    });
  } catch (error) {
    logError({
      message: 'Legacy Android (API<29) SAF download error',
      errorMessage: error instanceof Error ? error.message : String(error),
      operationName: 'saveToLegacyAndroidDownloads',
    });
    return false;
  }
};
